import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { queryKeys } from '~/queryClient'
import { FinancingStatus, FinancingSteps } from '~/services-v2/financial-bff/__generated__/zeus'
import financingBff from '~/services-v2/financial-bff/financial-bff'

export interface UseFinancingsParams {
  query: string
  page: number
  partner_id: number | string
  steps: FinancingSteps[]
  status: FinancingStatus[]
  is_combo: string
  is_combo_connect?: boolean
  page_size: number
  order_by: string
  provider?: string
  is_installed?: boolean
  formalized_lte?: number
  formalized_gte?: number
  is_expired?: boolean
  is_canceled?: boolean
}

export {
  FinancingStatus,
  FinancingSteps,
}

function createFinancingsQueryFn(params: UseFinancingsParams) {
  return async () => {
    const partnerId = params.partner_id === '' || params.partner_id === null || params.partner_id === undefined
      ? undefined
      : params.partner_id.toString()

    const res = await financingBff('query')({
      financings: [{
        input: {
          is_combo: params.is_combo,
          is_combo_connect: params.is_combo_connect,
          order_by: params.order_by,
          page: params.page,
          page_size: params.page_size,
          partner_id: partnerId,
          query: params.query,
          status: params.status,
          steps: params.steps,
          provider: params.provider,
          is_installed: params.is_installed,
          formalized_lte: params.formalized_lte,
          formalized_gte: params.formalized_gte,
          is_expired: params.is_expired,
          is_canceled: params.is_canceled,
        },
      }, {
        data: {
          id: true,
          customer_name: true,
          financed_value: true,
          project_value: true,
          step: true,
          status: true,
          project_id: true,
          document: true,
          expires_at: true,
          created_at: true,
          formalized_at: true,
          installed_at: true,
          is_combo: true,
          is_combo_connect: true,
          flow_provider: true,
          person_type: true,
          all_payments_sent: true,
          updated_at: true,
          is_expired: true,
          system_power: true,
          new_journey: true,
        },
        has_next: true,
        all_steps: {
          name: true,
          value: true,
        },
        statuses: true,
      }],
    })

    return res.financings
  }
}

export default function useFinancings(params: Ref<UseFinancingsParams>) {
  const queryKey = ref(queryKeys.FINANCINGS.LIST(params.value))
  const queryClient = useQueryClient()

  const queryFn = () => createFinancingsQueryFn(params.value)()

  const query = useQuery({
    queryKey,
    queryFn,
    staleTime: 1000 * 30,
  })

  watch(params, () => {
    queryKey.value = queryKeys.FINANCINGS.LIST(params.value)
  }, { deep: true })

  const prefetchPage = (pageNumber: number) => {
    const prefetchParams = { ...params.value, page: pageNumber }
    const prefetchQueryKey = queryKeys.FINANCINGS.LIST(prefetchParams)
    const existingData = queryClient.getQueryData(prefetchQueryKey)
    if (!existingData) {
      queryClient.prefetchQuery({
        queryKey: prefetchQueryKey,
        queryFn: createFinancingsQueryFn(prefetchParams),
        staleTime: 1000 * 30,
      })
    }
  }

  return {
    ...query,
    prefetchPage,
  }
}

export type FinancingsList = ReturnType<typeof useFinancings>['data']['value']
